import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { ModuleBoundary } from "@/features/apps/module-boundary";
import { useSession } from "@/lib/session/use-session";

export const CATALOG_PERMISSIONS = {
  productRead: "catalog.product.read",
  productCreate: "catalog.product.create",
  productUpdate: "catalog.product.update",
  variantRead: "catalog.variant.read",
  attributeRead: "catalog.attribute.read",
  attributeCreate: "catalog.attribute.create",
  attributeUpdate: "catalog.attribute.update",
  attributeDelete: "catalog.attribute.delete",
  barcodeRead: "catalog.barcode.read",
  barcodeCreate: "catalog.barcode.create",
  barcodeDelete: "catalog.barcode.delete",
  componentRead: "catalog.component.read",
  componentCreate: "catalog.component.create",
  componentDelete: "catalog.component.delete",
  uomRead: "catalog.uom.read",
  uomCreate: "catalog.uom.create",
  uomUpdate: "catalog.uom.update",
  uomDelete: "catalog.uom.delete",
  categoryRead: "catalog.category.read",
  taxClassRead: "catalog.tax_class.read",
  taxClassCreate: "catalog.tax_class.create",
  taxClassUpdate: "catalog.tax_class.update",
  taxClassDelete: "catalog.tax_class.delete",
  priceListRead: "catalog.price_list.read",
  priceListCreate: "catalog.price_list.create",
  priceListUpdate: "catalog.price_list.update",
  priceListDelete: "catalog.price_list.delete",
  importRead: "catalog.import.read",
  importCreate: "catalog.import.create",
  importCommit: "catalog.import.commit",
  importDelete: "catalog.import.delete",
} as const;

export type CatalogPermission = (typeof CATALOG_PERMISSIONS)[keyof typeof CATALOG_PERMISSIONS];

/** Direct-route authorization: denied pages never mount or issue queries. */
export function CatalogPermissionBoundary({
  permission,
  children,
}: {
  permission: CatalogPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();

  // The app comes before the grant, and in that order deliberately. If the
  // company has not installed this app, saying "you lack permission" sends
  // somebody to an administrator who cannot help — the fix is to install it,
  // not to be granted something. The server answers the same way round.
  if (!hasModule("catalog")) return <ModuleBoundary app="catalog">{children}</ModuleBoundary>;

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="catalog-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="catalog-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <p className="font-mono text-xs font-semibold tracking-[0.18em] text-destructive">
              {t("catalog.permissions.code", "403 · ACCESS CONTROL")}
            </p>
            <h1 id="catalog-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("catalog.permissions.title", "Catalog access restricted")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
